import { TFunction } from 'i18next';

//What a GitHub connection holds (the API's GitHubAuthData, written by the install's complete call): the installation of
//the ISOPlanner GitHub App on the customer's organization. Nothing in it is secret; the web only shows it.
//The API's GitHubInstallationProblems: why the last connection test failed on the installation
export enum GitHubInstallationProblems {
  Uninstalled = 'Uninstalled',
  Suspended = 'Suspended',
  PermissionsMissing = 'PermissionsMissing',
}

export class GitHubAuthData {
  installationId: number = 0;

  organization: string = '';

  organizationId: number = 0;

  //empty when the last connection test passed or never ran
  problem: string = '';

  static from(authData: unknown): GitHubAuthData {
    const output = new GitHubAuthData();
    const raw = (authData ?? {}) as Partial<GitHubAuthData>;
    output.installationId = raw.installationId ?? 0;
    output.organization = raw.organization ?? '';
    output.organizationId = raw.organizationId ?? 0;
    output.problem = raw.problem ?? '';

    return output;
  }

  //the installation's settings on GitHub: the repository selection, a permission change to accept, or the uninstall
  getManageUrl(): string {
    return `https://github.com/organizations/${encodeURIComponent(this.organization)}/settings/installations/${
      this.installationId
    }`;
  }
}

export enum GitHubSelectionModes {
  All = 'All',
  Selected = 'Selected',
}

//The API caps the named repositories of a selection at this number
export const gitHubMaxNamedRepositories = 100;

const repositoryNamePattern = /^[A-Za-z0-9._-]{1,100}$/;
const branchNamePattern = /^[^\s~^:?*[\\]{1,255}$/;

export const isGitHubRepositoryName = (name: string): boolean => repositoryNamePattern.test(name);

//a branch rule's value: a branch name, no pattern; matching one needs the branch list, which needs Contents read
//(PLAN-FBL-002 amendment 1)
export const isGitHubBranchName = (name: string): boolean => branchNamePattern.test(name);

//Which repositories a GitHub repository check evaluates (the API's GitHubRepositorySelection, nested in the config of
//every repository type). The candidates are the repositories the organization granted the app's installation.
export class GitHubRepositorySelection {
  mode: GitHubSelectionModes = GitHubSelectionModes.All;

  //Mode Selected: the repository names, without the owner
  repositories: string[] = [];

  //Mode All: names left out
  excludedRepositories: string[] = [];

  includeArchived: boolean = false;

  includeForks: boolean = false;

  //empty: each repository's default branch
  branch: string = '';

  static from(stored: unknown): GitHubRepositorySelection {
    const raw = (stored ?? {}) as Omit<Partial<GitHubRepositorySelection>, 'mode'> & { mode?: unknown };
    const selection = new GitHubRepositorySelection();
    //the API's enum arrives as its name; an old number 1 is Selected
    selection.mode =
      raw.mode === GitHubSelectionModes.Selected || raw.mode === 1
        ? GitHubSelectionModes.Selected
        : GitHubSelectionModes.All;
    selection.repositories = Array.isArray(raw.repositories) ? [...raw.repositories] : [];
    selection.excludedRepositories = Array.isArray(raw.excludedRepositories) ? [...raw.excludedRepositories] : [];
    selection.includeArchived = raw.includeArchived ?? false;
    selection.includeForks = raw.includeForks ?? false;
    selection.branch = raw.branch ?? '';

    return selection;
  }

  //the names trimmed, without empties and without case-insensitive duplicates, as the API reads them
  static cleanNames(names: string[]): string[] {
    const seen = new Set<string>();
    const output: string[] = [];
    for (const name of names) {
      const trimmed = name.trim();
      if (!trimmed || seen.has(trimmed.toLowerCase())) continue;
      seen.add(trimmed.toLowerCase());
      output.push(trimmed);
    }

    return output;
  }

  isEqual(other: GitHubRepositorySelection): boolean {
    const sameNames = (a: string[], b: string[]): boolean =>
      a.length === b.length && a.every((name, index) => name.toLowerCase() === b[index].toLowerCase());

    return (
      this.mode === other.mode &&
      sameNames(
        GitHubRepositorySelection.cleanNames(this.repositories),
        GitHubRepositorySelection.cleanNames(other.repositories),
      ) &&
      sameNames(
        GitHubRepositorySelection.cleanNames(this.excludedRepositories),
        GitHubRepositorySelection.cleanNames(other.excludedRepositories),
      ) &&
      this.includeArchived === other.includeArchived &&
      this.includeForks === other.includeForks &&
      this.branch.trim() === other.branch.trim()
    );
  }

  //The structural problems, as the API's GitHubRepositorySelectionRules sees them; empty when valid. Whether a named
  //repository is granted to the app is the API's to say (validate before the test run).
  getProblems(t: TFunction<string[]>): string[] {
    const problems: string[] = [];
    const repositories = GitHubRepositorySelection.cleanNames(this.repositories);
    const excluded = GitHubRepositorySelection.cleanNames(this.excludedRepositories);

    if (this.mode === GitHubSelectionModes.Selected && repositories.length === 0) {
      problems.push(t('integrations:GitHubBaseline.Selection.RepositoriesRequired'));
    }

    if (repositories.length > gitHubMaxNamedRepositories || excluded.length > gitHubMaxNamedRepositories) {
      problems.push(t('integrations:GitHubBaseline.Selection.TooMany', { max: gitHubMaxNamedRepositories }));
    }

    const invalid = [...repositories, ...excluded].filter((name) => !repositoryNamePattern.test(name));
    if (invalid.length > 0) {
      problems.push(t('integrations:GitHubBaseline.Selection.InvalidNames', { names: invalid.join(', ') }));
    }

    if (this.branch.trim() && !branchNamePattern.test(this.branch.trim())) {
      problems.push(t('integrations:GitHubBaseline.Selection.InvalidBranch'));
    }

    return problems;
  }
}

//One evaluated repository in the evidence of a GitHub repository check; a type adds its own values
export class GitHubRepositoryResult {
  name: string = '';

  branch: string | undefined = undefined;

  visibility: string | undefined = undefined;

  passed: boolean = false;

  //stable problem codes; empty when passed
  problems: string[] = [];
}

export const readGitHubRepositoryResult = <T extends GitHubRepositoryResult>(
  target: T,
  item: Record<string, unknown>,
): T => {
  target.name = typeof item.name === 'string' ? item.name : '';
  target.branch = typeof item.branch === 'string' ? item.branch : undefined;
  target.visibility = typeof item.visibility === 'string' ? item.visibility : undefined;
  target.passed = item.passed === true;
  target.problems = Array.isArray(item.problems) ? item.problems.filter((code) => typeof code === 'string') : [];

  return target;
};
